import { z } from 'zod/v4';

// ─── Messages ─────────────────────────────────────────────────────────────────

export interface HandlingUnitTypeSchemaMessages {
  codeFormat: string;
  codeRequired: string;
  labelRequired: string;
  positiveNumber: string;
  volumeRequired: string;
  weightRequired: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isPositiveNumber(value: string): boolean {
  const parsed = parseFloat(value);
  return !isNaN(parsed) && parsed > 0;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export function createHandlingUnitTypeSchema(
  isEditMode: boolean,
  messages: HandlingUnitTypeSchemaMessages,
): z.ZodObject<{
  code: z.ZodString;
  label: z.ZodString;
  volumeUnit: z.ZodEnum<{ L: 'L'; m3: 'm3' }>;
  volumeValue: z.ZodString;
  weightUnit: z.ZodEnum<{ kg: 'kg'; lb: 'lb' }>;
  weightValue: z.ZodString;
}> {
  const codeSchema = isEditMode
    ? z.string().trim().min(1, messages.codeRequired)
    : z
        .string()
        .trim()
        .min(1, messages.codeRequired)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, messages.codeFormat);

  return z.object({
    code: codeSchema,
    label: z.string().trim().min(1, messages.labelRequired),
    volumeUnit: z.enum(['L', 'm3']),
    volumeValue: z.string().min(1, messages.volumeRequired).refine(isPositiveNumber, messages.positiveNumber),
    weightUnit: z.enum(['kg', 'lb']),
    weightValue: z.string().min(1, messages.weightRequired).refine(isPositiveNumber, messages.positiveNumber),
  });
}

export type HandlingUnitTypeFormInput = z.infer<ReturnType<typeof createHandlingUnitTypeSchema>>;
